// Pantalla de configuración — Campos del formulario de disco.
// Solo accesible por propietarios (user_admin). Dos bloques:
//   1. Campos estándar: casillas para marcar cuáles son obligatorios al
//      ingresar/editar un disco de esta cuenta.
//   2. Campos personalizados: el usuario define etiqueta, tipo, en qué pantallas
//      se piden y si son obligatorios. Se persisten en el modelo de datos y
//      aparecen en la descarga del Excel del catálogo.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../api/ApiContext.js';
import type { CustomFieldDef, CustomFieldType, CustomFieldScreen } from '../../api/endpoints.js';
import { AlertMessage, Card, Icon, PageHeader, Spinner } from '../../ui/components.js';
import { useAsync } from '../../ui/useAsync.js';

export function RecordFieldsView() {
  const { t } = useTranslation();
  const api = useApi();
  const config = useAsync(() => api.config.getRecordFields(), []);
  const [required, setRequired] = useState<string[] | null>(null);
  const [custom, setCustom] = useState<CustomFieldDef[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const standardFields = config.data?.standardFields ?? [];
  const requiredSet = required ?? config.data?.requiredStandard ?? [];
  const customFields = custom ?? config.data?.customFields ?? [];
  const typeOptions = config.data?.customFieldTypes ?? ['text', 'number', 'boolean', 'date', 'select'];
  const screenOptions = config.data?.customFieldScreens ?? ['add', 'edit'];

  function toggleRequired(field: string) {
    setSaved(false);
    const base = required ?? config.data?.requiredStandard ?? [];
    setRequired(base.includes(field) ? base.filter((f) => f !== field) : [...base, field]);
  }

  function updateCustom(next: CustomFieldDef[]) {
    setSaved(false);
    setCustom(next);
  }

  function addCustom() {
    updateCustom([
      ...customFields,
      { key: '', label: '', type: 'text', screens: ['add', 'edit'], required: false },
    ]);
  }

  function patchCustom(idx: number, patch: Partial<CustomFieldDef>) {
    updateCustom(customFields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function removeCustom(idx: number) {
    updateCustom(customFields.filter((_, i) => i !== idx));
  }

  function toggleScreen(idx: number, screen: CustomFieldScreen) {
    const f = customFields[idx]!;
    const screens = f.screens.includes(screen)
      ? f.screens.filter((s) => s !== screen)
      : [...f.screens, screen];
    patchCustom(idx, { screens });
  }

  async function save() {
    // Validación ligera en cliente: toda etiqueta debe venir.
    if (customFields.some((f) => f.label.trim() === '')) {
      setError(t('recordFields.errLabelRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await api.config.setRecordFields({
        requiredStandard: requiredSet,
        // Las claves vacías las deriva el backend desde la etiqueta (slug).
        customFields: customFields.map((f) => ({
          key: f.key,
          label: f.label.trim(),
          type: f.type,
          screens: f.screens,
          required: f.required,
          ...(f.type === 'select' && f.options ? { options: f.options } : {}),
        })),
      });
      // Resincroniza con lo que el backend saneó (claves generadas, etc.).
      setRequired(result.requiredStandard);
      setCustom(result.customFields);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordFields.errSave'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('recordFields.pageTitle')}
        subtitle={t('recordFields.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <Card title={t('recordFields.cardTitle')} icon="dynamic_form">
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
            {t('recordFields.cardHint')}
          </p>

          {config.loading && <Spinner label={t('recordFields.loading')} />}
          {config.error && <p className="text-error font-body-sm text-body-sm">{String(config.error)}</p>}

          {!config.loading && config.data && (
            <div className="flex flex-col gap-space-md">
              {/* Campos estándar obligatorios. */}
              <div>
                <p className="section-label text-on-surface-variant mb-space-xs">{t('recordFields.requiredFields')}</p>
                <div className="grid grid-cols-2 gap-space-xs">
                  {standardFields.map((f) => {
                    const checked = requiredSet.includes(f.field);
                    return (
                      <label
                        key={f.field}
                        className={`flex items-center gap-space-xs px-space-sm h-10 rounded-lg border cursor-pointer transition-colors ${
                          checked
                            ? 'border-primary-container bg-primary-container/10 text-primary'
                            : 'border-border-subtle text-on-surface-variant hover:border-outline'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-primary"
                          checked={checked}
                          onChange={() => toggleRequired(f.field)}
                        />
                        <span className="font-body-sm text-body-sm truncate">{f.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Campos personalizados. */}
              <div>
                <div className="flex items-center justify-between mb-space-xs">
                  <p className="section-label text-on-surface-variant">{t('recordFields.customFields')}</p>
                  <button className="btn-ghost btn-sm" type="button" onClick={addCustom}>
                    <Icon name="add" className="text-[16px]" />
                    {t('recordFields.addField')}
                  </button>
                </div>

                {customFields.length === 0 && (
                  <p className="font-body-sm text-body-sm text-on-surface-variant italic mb-space-sm">
                    {t('recordFields.noCustomFields')}
                  </p>
                )}

                <div className="flex flex-col gap-space-sm">
                  {customFields.map((f, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border-subtle p-space-sm flex flex-col gap-space-xs"
                    >
                      <div className="flex items-end gap-space-xs">
                        <div className="flex flex-col flex-1 gap-space-xs">
                          <label className="field-label">{t('recordFields.fieldName')}</label>
                          <input
                            className="field-input bg-surface-deck rounded border border-border-subtle px-2 h-9"
                            placeholder={t('recordFields.fieldNamePlaceholder')}
                            value={f.label}
                            onChange={(e) => patchCustom(idx, { label: e.target.value })}
                          />
                        </div>
                        <div className="flex flex-col gap-space-xs">
                          <label className="field-label">{t('recordFields.fieldType')}</label>
                          <select
                            className="field-input h-9 bg-surface-deck rounded border border-border-subtle px-2"
                            value={f.type}
                            onChange={(e) => patchCustom(idx, { type: e.target.value as CustomFieldType })}
                          >
                            {typeOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {t(`recordFields.type.${opt}`)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          className="btn-ghost btn-sm text-error shrink-0 h-9"
                          type="button"
                          onClick={() => removeCustom(idx)}
                          title={t('recordFields.deleteField')}
                        >
                          <Icon name="delete" className="text-[16px]" />
                        </button>
                      </div>
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        {t('recordFields.fieldNameHint')}
                      </p>

                      {f.type === 'select' && (
                        <input
                          className="field-input bg-surface-deck rounded border border-border-subtle px-2 h-9"
                          placeholder={t('recordFields.selectOptionsPlaceholder')}
                          value={(f.options ?? []).join(', ')}
                          onChange={(e) =>
                            patchCustom(idx, {
                              options: e.target.value
                                .split(',')
                                .map((o) => o.trim())
                                .filter((o) => o !== ''),
                            })
                          }
                        />
                      )}

                      <div className="flex items-center gap-space-md flex-wrap">
                        <span className="font-body-sm text-body-sm text-on-surface-variant">{t('recordFields.askOn')}</span>
                        {screenOptions.map((s) => (
                          <label key={s} className="flex items-center gap-space-xs cursor-pointer">
                            <input
                              type="checkbox"
                              className="accent-primary"
                              checked={f.screens.includes(s)}
                              onChange={() => toggleScreen(idx, s)}
                            />
                            <span className="font-body-sm text-body-sm">{t(`recordFields.screen.${s}`)}</span>
                          </label>
                        ))}
                        <label className="flex items-center gap-space-xs cursor-pointer ml-auto">
                          <input
                            type="checkbox"
                            className="accent-primary"
                            checked={f.required}
                            onChange={(e) => patchCustom(idx, { required: e.target.checked })}
                          />
                          <span className="font-body-sm text-body-sm">{t('recordFields.required')}</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <AlertMessage message={error} />
              {saved && <p className="text-goldmine-mint font-body-sm text-body-sm">{t('recordFields.saved')}</p>}

              <button
                className="btn-primary w-full"
                type="button"
                disabled={saving}
                onClick={() => void save()}
              >
                <Icon name="save" className="text-[18px]" />
                {saving ? t('recordFields.saving') : t('recordFields.save')}
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
